import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  Box,
  Typography,
  TextField,
  Button,
  Collapse,
  Checkbox,
  Grid,
  Stack,
  IconButton,
  LinearProgress
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  getOnboardingDetailsById,
  updateOnboardingTask,
  updateTaskStatus,
  getTaskHistoryLog
} from 'src/services/onboardingService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import StatusDropdown from './StatusDropdown';
import CustomToggleSwitch from './CustomToggleSwitch';
import { withStyles } from '@mui/styles';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { TableColumnCell } from 'src/components/TableColumnCell';
import { AppActions } from 'src/store/AppReducer';
import { useDispatch } from 'react-redux';
import {
  TaskDetail,
  OnboardingTaskListProps as Props,
  taskDependencies,
  SubTaskDetail,
  TaskHistory
} from './interface/onboardingInterfaces';
import { validateMultiRolesAndPermissions } from 'src/utils/validate';
import { getRolePermissions } from 'src/services/authService';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { getLeaveForSelectedMonth } from 'src/services/yearPlannerService';

const labelToApi = (label: string) => {
  switch (label) {
    case 'To Do':
      return 'TO_DO';
    case 'In Progress':
      return 'IN_PROGRESS';
    case 'On Hold':
      return 'ON_HOLD';
    case 'Completed':
      return 'COMPLETED';
    default:
      return 'TO_DO';
  }
};

const apiToLabel = (api: string) => {
  switch ((api || '').toUpperCase()) {
    case 'TO_DO':
      return 'To Do';
    case 'IN_PROGRESS':
      return 'In Progress';
    case 'ON_HOLD':
      return 'On Hold';
    case 'COMPLETED':
      return 'Completed';
    default:
      return 'To Do';
  }
};

const normalizeTaskStatus = (status?: string | null) =>
  (status || '').toUpperCase().replace(/\s+/g, '_');

const HOLIDAY_FULL_DAY = 0;
const HOLIDAY_MORNING_HALF = 1;
const HOLIDAY_EVENING_HALF = 2;

const getDateKey = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const mergeDayType = (existing?: number, incoming?: number): number | undefined => {
  if (incoming === undefined || incoming === null) return existing;
  if (existing === undefined || existing === null) return incoming;
  if (existing === HOLIDAY_FULL_DAY || incoming === HOLIDAY_FULL_DAY) {
    return HOLIDAY_FULL_DAY;
  }
  // If both half-days exist for the same date, treat as full-day leave.
  if (
    (existing === HOLIDAY_MORNING_HALF && incoming === HOLIDAY_EVENING_HALF) ||
    (existing === HOLIDAY_EVENING_HALF && incoming === HOLIDAY_MORNING_HALF)
  ) {
    return HOLIDAY_FULL_DAY;
  }
  return existing;
};

const parseSlaToMs = (sla?: string | number | null): number | null => {
  if (sla === null || sla === undefined) return null;

  if (typeof sla === 'number') {
    // API sends SLA as minutes for numeric values.
    return Number.isFinite(sla) ? sla * 60 * 1000 : null;
  }

  const raw = String(sla).trim();
  if (!raw) return null;

  // Numeric string payload from API is in minutes.
  if (/^\d+$/.test(raw)) {
    return parseInt(raw, 10) * 60 * 1000;
  }

  // "Xd HH:MM:SS" or "HH:MM:SS"
  const match = raw.match(/^(?:(\d+)d\s+)?(\d{1,2}):(\d{2}):(\d{2})$/i);
  if (!match) return null;

  const days = parseInt(match[1] || '0', 10);
  const hours = parseInt(match[2] || '0', 10);
  const minutes = parseInt(match[3] || '0', 10);
  const seconds = parseInt(match[4] || '0', 10);

  if ([days, hours, minutes, seconds].some((x) => Number.isNaN(x))) {
    return null;
  }

  const workingMsPerDay = 9 * 60 * 60 * 1000;
  return (
    days * workingMsPerDay +
    hours * 60 * 60 * 1000 +
    minutes * 60 * 1000 +
    seconds * 1000
  );
};

const formatMMDDYYYY = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const OFFICIAL_EMAIL_DOMAIN = '@digiratina.com';

const isValidOfficialEmail = (s: string) => {
  if (!s) return false;
  const email = s.trim().toLowerCase();
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (!local || !domain) return false;
  return email.endsWith(OFFICIAL_EMAIL_DOMAIN);
};

const getDefaultOfficialEmail = (email?: string) => {
  const value = (email || '').trim();
  if (value.toLowerCase().endsWith(OFFICIAL_EMAIL_DOMAIN) && value.includes('@')) {
    return value;
  }
  return OFFICIAL_EMAIL_DOMAIN;
};

const errorTextStyle = {
  color: '#fc2424ff',
  fontSize: '12px',
  marginTop: '4px',
  fontWeight: 500
};

const OnboardingTaskList: React.FC<Props> = ({
  candidate,
  expectedJoinDate,
  hasOnboardingStarted,
  onOrientationStatusChange
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const candidateId = candidate?.id;
  const [onboardingDetails, setOnboardingDetails] = useState<any>(null);
  const [orderedTaskList, setOrderedTaskList] = useState<TaskDetail[]>([]);
  const [taskDependencyList, setTaskDependencyList] = useState<
    taskDependencies[]
  >([]);
  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null);
  const [taskValues, setTaskValues] = useState<
    Record<number, Record<string, string>>
  >({});
  const [taskCompletion, setTaskCompletion] = useState<Record<number, boolean>>(
    {}
  );
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [fieldErrors, setFieldErrors] = useState<
    Record<
      number,
      { officialEmail?: string; comment?: string; assetId?: string }
    >
  >({});
  const [savingTaskId, setSavingTaskId] = useState<number | null>(null);
  const [preOnboardingTasks, setPreOnboardingTasks] = useState<TaskDetail[]>(
    []
  );
  const [postOnboardingTasks, setPostOnboardingTasks] = useState<TaskDetail[]>(
    []
  );
  const [preExpanded, setPreExpanded] = useState(true);
  const [postExpanded, setPostExpanded] = useState(true);
  const [
    preOnboardingCompletedTasksCount,
    setPreOnboardingCompletedTasksCount
  ] = useState<number>(0);
  const [
    postOnboardingCompletedTasksCount,
    setPostOnboardingCompletedTasksCount
  ] = useState<number>(0);
  const [timeLeftMap, setTimeLeftMap] = useState<Record<number, string>>({});
  const [statusChangedFlag, setStatusChangedFlag] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [holidayDayTypeByDate, setHolidayDayTypeByDate] = useState<
    Record<string, number>
  >({});
  const [historyVisibility, setHistoryVisibility] = useState<
    Record<number, boolean>
  >({});
  const [taskHistory, setTaskHistory] = useState<Record<number, TaskHistory[]>>(
    {}
  );
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const frozenTimerStorageKey = `onboardingFrozenTimers-${candidateId || 'unknown'}`;

  const WORK_START = 9;
  const WORK_END = 18;
  const MS_IN_HOUR = 1000 * 60 * 60;
  const MS_IN_MINUTE = 1000 * 60;
  const MS_IN_SECOND = 1000;
  const HALF_DAY_SPLIT_HOUR = 13.5; // 1:30 PM
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem',
      width: '4.2rem'
    }
  })(TableRow);

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPage(0);
    setLimit(parseInt(event.target.value));
  };

  const handleSuccess = (message: string) => {
    setSnackBarMessage(message);
    setSnackbarSeverity('success');
    setSnackBarOpen(true);
  };
  const handleError = (message: string) => {
    setSnackBarMessage(message);
    setSnackbarSeverity('error');
    setSnackBarOpen(true);
  };

  const getFrozenTimerMap = useCallback((): Record<number, string> => {
    try {
      const raw = localStorage.getItem(frozenTimerStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }, [frozenTimerStorageKey]);

  const setFrozenTimerValue = useCallback(
    (taskId: number, value: string) => {
      const current = getFrozenTimerMap();
      const next = { ...current, [taskId]: value };
      localStorage.setItem(frozenTimerStorageKey, JSON.stringify(next));
    },
    [frozenTimerStorageKey, getFrozenTimerMap]
  );

  const removeFrozenTimerValue = useCallback(
    (taskId: number) => {
      const current = getFrozenTimerMap();
      if (!(taskId in current)) return;
      const next = { ...current };
      delete next[taskId];
      localStorage.setItem(frozenTimerStorageKey, JSON.stringify(next));
    },
    [frozenTimerStorageKey, getFrozenTimerMap]
  );

  const getPermissions = (mail) => {
    getRolePermissions(mail)
      .then((res) => {
        const tabs = validateMultiRolesAndPermissions(res);
        const arr = tabs.filter((fil) => fil.name === 'Onboarding To Do List');

        if (arr.length > 0) {
          setPermissions(arr.length > 0 ? arr[0].permissions : []);
        } else {
          setPermissions([]);
        }
      })
      .catch((error) => {
        setSnackBarMessage('Failed to fetch permissions');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
        setPermissions([]);
      });
  };

  const getOverallStatusLabel = (tasks: any[]) => {
    if (tasks.some((task) => task.status === 'IN_PROGRESS')) {
      return 'In progress';
    } else if (tasks.every((task) => task.status === 'COMPLETED')) {
      return 'Completed';
    } else {
      return 'Pending';
    }
  };

  const statusLabelWidth = useMemo(() => {
    const labels = [
      getOverallStatusLabel(preOnboardingTasks),
      getOverallStatusLabel(postOnboardingTasks)
    ];
    const maxLength = Math.max(...labels.map((label) => label.length));
    // 9px per character + 24px padding, but minimum 90px for consistency
    return Math.max(maxLength * 8 + 16, 70);
  }, [preOnboardingTasks, postOnboardingTasks]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (Object.keys(user).length === 0) {
      navigate('/');
    } else {
      getPermissions(user.Email);
    }
  }, []);

  useEffect(() => {
    if (savingTaskId !== null) return;

    const tryLoad = async (id: number) => {
      const res = await getOnboardingDetailsById(id);
      return typeof res === 'string' ? JSON.parse(res) : res;
    };

    const fetchOnboardingDetails = async () => {
      try {
        const onboardingResponse = await tryLoad(parseInt(candidateId));
        setOnboardingDetails(onboardingResponse.data.onboardingDetails);
        const emailAccountTask = onboardingResponse.data.taskDetails.find(
          (t) => t.name?.toLowerCase() === 'create an email account'
        );

        if (onOrientationStatusChange) {
          onOrientationStatusChange(
            normalizeTaskStatus(emailAccountTask?.status) === 'COMPLETED'
          );
        }

        const taskList = onboardingResponse.data.taskDetails ?? [];
        const sortedTasks = [...taskList].sort(
          (a, b) => (a.priority ?? 9999) - (b.priority ?? 9999)
        );
        setOrderedTaskList(sortedTasks);
        setTaskDependencyList(
          onboardingResponse.data.taskDependencyDetails ?? []
        );

        setTaskCompletion(
          Object.fromEntries(
            sortedTasks.flatMap((task) => {
              const taskEntry: [string, boolean] = [
                `task-${task.id}`,
                task.status === 'COMPLETED'
              ];

              const subTaskEntries: [string, boolean][] =
                task.subTaskDetails?.map((sub) => [
                  `subtask-${sub.id}`,
                  sub.status === 'COMPLETED'
                ]) ?? [];

              return [taskEntry, ...subTaskEntries];
            })
          )
        );
      } catch (err) {
        setSnackBarMessage('Failed to load onboarding tasks.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      }
    };

    fetchOnboardingDetails();
  }, [
    candidateId,
    statusChangedFlag,
    hasOnboardingStarted,
    savingTaskId,
    onOrientationStatusChange
  ]);

  useEffect(() => {
    if (!orderedTaskList.length) return;

    setPreOnboardingTasks(orderedTaskList.filter((t) => t.isPeronboarding));
    setPostOnboardingTasks(orderedTaskList.filter((t) => t.isPostonboarding));

    setTaskValues((prev) => {
      const newValues = { ...prev };

      orderedTaskList.forEach((task) => {
        const mainFields = getFieldsForTask(task.name);
        if (!newValues[task.id]) {
          newValues[task.id] = {};
          mainFields.forEach((f) => {
            if (f.key === 'officialEmail') {
              newValues[task.id][f.key] = getDefaultOfficialEmail(
                candidate?.officialEmail
              );
            } else if (f.key === 'assetId') {
              newValues[task.id][f.key] = task?.assertId ?? '';
            } else {
              newValues[task.id][f.key] = task[f.key] ?? '';
            }
          });
        }

        task.subTaskDetails?.forEach((sub) => {
          const subFields = getFieldsForTask(sub.name);
          if (!newValues[sub.id]) {
            newValues[sub.id] = {};
            subFields.forEach((f) => {
              newValues[sub.id][f.key] = sub[f.key] ?? '';
            });
          }
        });
      });

      return newValues;
    });
  }, [orderedTaskList]);

  useEffect(() => {
    setPreOnboardingCompletedTasksCount(
      preOnboardingTasks.filter((t) => t.status === 'COMPLETED').length
    );
    setPostOnboardingCompletedTasksCount(
      postOnboardingTasks.filter((t) => t.status === 'COMPLETED').length
    );
  }, [preOnboardingTasks, postOnboardingTasks]);

  const taskListRef = React.useRef<TaskDetail[]>([]);
  const timeLeftMapRef = React.useRef<Record<number, string>>({});
  const holidayDayTypeByDateRef = React.useRef<Record<string, number>>({});

  useEffect(() => {
    taskListRef.current = orderedTaskList;
  }, [orderedTaskList]);

  useEffect(() => {
    timeLeftMapRef.current = timeLeftMap;
  }, [timeLeftMap]);

  useEffect(() => {
    holidayDayTypeByDateRef.current = holidayDayTypeByDate;
  }, [holidayDayTypeByDate]);

  useEffect(() => {
    if (!hasOnboardingStarted || !orderedTaskList.length) return;

    const activeTasks = orderedTaskList.filter(
      (task) => normalizeTaskStatus(task.status) !== 'ON_HOLD'
    );

    if (!activeTasks.length) {
      setHolidayDayTypeByDate({});
      return;
    }

    const now = new Date();
    const dueDates = activeTasks
      .map((task) => new Date(task.expectedCompletionAt))
      .filter((d) => !isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    const maxDueDate = dueDates[dueDates.length - 1];
    if (!maxDueDate) {
      setHolidayDayTypeByDate({});
      return;
    }

    const monthsToQuery: { month: number; year: number }[] = [];
    const cursor = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(maxDueDate.getFullYear(), maxDueDate.getMonth(), 1);

    while (cursor.getTime() <= end.getTime()) {
      monthsToQuery.push({ month: cursor.getMonth() + 1, year: cursor.getFullYear() });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const loadHolidays = async () => {
      try {
        const responses = await Promise.all(
          monthsToQuery.map(({ month, year }) => getLeaveForSelectedMonth(month, year))
        );

        const nextMap: Record<string, number> = {};

        responses.forEach((res: any) => {
          const payload = res?.data?.data ?? res?.data ?? [];
          if (!Array.isArray(payload)) return;

          payload.forEach((item: any) => {
            const d = new Date(item?.eventDate);
            if (isNaN(d.getTime())) return;
            const key = getDateKey(d);
            const dayType = Number(item?.dayType);
            if (Number.isNaN(dayType)) return;

            nextMap[key] = mergeDayType(nextMap[key], dayType) as number;
          });
        });

        setHolidayDayTypeByDate(nextMap);
      } catch {
        // Fallback: continue with weekday-only timer logic if holiday API fails.
        setHolidayDayTypeByDate({});
      }
    };

    loadHolidays();
  }, [orderedTaskList, hasOnboardingStarted]);

  // ...existing code...
  useEffect(() => {
    if (!hasOnboardingStarted) return;

    const tick = () => {
      const newTimeLeft: Record<number, string> = {};
      const currentTaskList = taskListRef.current;

      if (!currentTaskList?.length) return;

      currentTaskList.forEach((task) => {
        // IMPORTANT: Skip ON_HOLD tasks - they should NOT be recalculated
        if (normalizeTaskStatus(task.status) === 'ON_HOLD') {
          // Keep existing timer value or use task.sla to display
          const frozenTimers = getFrozenTimerMap();
          const existingValue =
            timeLeftMapRef.current[task.id] || frozenTimers[task.id] || '';

          // If we have task.sla, use it to display frozen time
          if (!existingValue) {
            const frozenMs = parseSlaToMs(task.sla);
            if (frozenMs !== null && frozenMs > 0) {
              const totalSeconds = Math.floor(frozenMs / 1000);
              const workingSecondsPerDay = 9 * 60 * 60;

              const days = Math.floor(totalSeconds / workingSecondsPerDay);
              const remainingSeconds = totalSeconds % workingSecondsPerDay;

              const hrs = Math.floor(remainingSeconds / 3600);
              const mins = Math.floor((remainingSeconds % 3600) / 60);
              const secs = remainingSeconds % 60;

              newTimeLeft[task.id] =
                days > 0
                  ? `${days}d ${String(hrs).padStart(2, '0')}:${String(
                      mins
                    ).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
                  : `${String(hrs).padStart(2, '0')}:${String(mins).padStart(
                      2,
                      '0'
                    )}:${String(secs).padStart(2, '0')}`;
            } else if (frozenMs !== null && frozenMs <= 0) {
              newTimeLeft[task.id] = 'Overdue';
              setFrozenTimerValue(task.id, newTimeLeft[task.id]);
            } else {
              newTimeLeft[task.id] = '';
            }
          } else if (existingValue) {
            // Keep the existing frozen value
            newTimeLeft[task.id] = existingValue;
            setFrozenTimerValue(task.id, existingValue);
          } else {
            // No value to display
            newTimeLeft[task.id] = '';
          }
          return; // Skip further calculation for ON_HOLD tasks
        }

        // Only calculate for non-ON_HOLD tasks
        const end = new Date(task.expectedCompletionAt);
        const hasValidDate = !isNaN(end.getTime());

        if (!hasValidDate) {
          newTimeLeft[task.id] = '';
          return;
        }

        // For active tasks, calculate normally
        const getRemainingWorkingMs = (endDate: Date): number => {
          const toDateAtHour = (base: Date, hour: number) => {
            const d = new Date(base);
            const h = Math.floor(hour);
            const m = Math.round((hour - h) * 60);
            d.setHours(h, m, 0, 0);
            return d;
          };

          const getWorkingWindows = (date: Date): Array<[number, number]> => {
            if (date.getDay() === 0 || date.getDay() === 6) return [];

            const dayType = holidayDayTypeByDateRef.current[getDateKey(date)];
            if (dayType === HOLIDAY_FULL_DAY) return [];
            if (dayType === HOLIDAY_MORNING_HALF) {
              return [[HALF_DAY_SPLIT_HOUR, WORK_END]];
            }
            if (dayType === HOLIDAY_EVENING_HALF) {
              return [[WORK_START, HALF_DAY_SPLIT_HOUR]];
            }

            return [[WORK_START, WORK_END]];
          };

          let now = new Date();
          let totalMs = 0;
          if (now > endDate) return 0;

          while (now < endDate) {
            const windows = getWorkingWindows(now);

            if (!windows.length) {
              now.setDate(now.getDate() + 1);
              now.setHours(WORK_START, 0, 0, 0);
              continue;
            }

            let advanced = false;

            for (const [windowStartHour, windowEndHour] of windows) {
              const windowStart = toDateAtHour(now, windowStartHour);
              const windowEnd = toDateAtHour(now, windowEndHour);

              if (endDate <= windowStart) {
                now = endDate;
                advanced = true;
                break;
              }

              if (now >= windowEnd) {
                continue;
              }

              const chunkStart = now > windowStart ? now : windowStart;
              const chunkEnd = endDate < windowEnd ? endDate : windowEnd;

              if (chunkStart < chunkEnd) {
                totalMs += chunkEnd.getTime() - chunkStart.getTime();
                now = chunkEnd;
                advanced = true;
              }

              if (now >= endDate) {
                break;
              }
            }

            if (now < endDate && !advanced) {
              now.setDate(now.getDate() + 1);
              now.setHours(WORK_START, 0, 0, 0);
            }
          }
          return totalMs;
        };

        const remaining = getRemainingWorkingMs(end);

        if (remaining <= 0) {
          newTimeLeft[task.id] = 'Overdue';
          return;
        }

        const totalSeconds = Math.floor(remaining / 1000);
        const workingSecondsPerDay = 9 * 60 * 60;

        const days = Math.floor(totalSeconds / workingSecondsPerDay);
        const remainingSeconds = totalSeconds % workingSecondsPerDay;

        const hrs = Math.floor(remainingSeconds / 3600);
        const mins = Math.floor((remainingSeconds % 3600) / 60);
        const secs = remainingSeconds % 60;

        newTimeLeft[task.id] =
          days > 0
            ? `${days}d ${String(hrs).padStart(2, '0')}:${String(mins).padStart(
                2,
                '0'
              )}:${String(secs).padStart(2, '0')}`
            : `${String(hrs).padStart(2, '0')}:${String(mins).padStart(
                2,
                '0'
              )}:${String(secs).padStart(2, '0')}`;
      });

      setTimeLeftMap(newTimeLeft);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [
    hasOnboardingStarted,
    statusChangedFlag,
    getFrozenTimerMap,
    setFrozenTimerValue
  ]);
  useEffect(() => {
    const fetchTaskHistories = async () => {
      for (const [taskId, isVisible] of Object.entries(historyVisibility)) {
        if (isVisible) {
          const res = await getTaskHistoryLog(parseInt(taskId));
          setTaskHistory((prev) => ({
            ...prev,
            [taskId]: res.data.data.map((historyItem: any) => ({
              ...historyItem,
              data: JSON.parse(historyItem.data)
            }))
          }));
        }
      }
    };

    fetchTaskHistories();
  }, [historyVisibility, statusChangedFlag]);

  const isOutsideHours = () => {
    const now = new Date();
    const hour = now.getHours() + now.getMinutes() / 60;
    const day = now.getDay();
    return hour < WORK_START || hour >= WORK_END || day === 0 || day === 6;
  };

  const handleExpand = (id: number) => {
    setExpandedTaskId(expandedTaskId === id ? null : id);
  };

  const handleInputChange = (taskId: number, key: string, value: string) => {
    setTaskValues((prev) => ({
      ...prev,
      [taskId]: { ...(prev[taskId] || {}), [key]: value }
    }));

    setFieldErrors((prev) => {
      if (!prev[taskId] || !prev[taskId][key]) return prev;
      return {
        ...prev,
        [taskId]: { ...prev[taskId], [key]: '' }
      };
    });
  };

  const validateTask = (task: TaskDetail | SubTaskDetail, values: any) => {
    const errors: Record<string, string> = {};

    if (task.name === 'Create an Email Account') {
      const email = (values.officialEmail || '').trim();
      if (!email || email === OFFICIAL_EMAIL_DOMAIN) {
        errors.officialEmail = 'Official email is required';
      } else if (!isValidOfficialEmail(email)) {
        errors.officialEmail =
          'Official email must end with @digiratina.com';
      }
    }

    if (task.name === 'Assign a Laptop') {
      const assetId = (values.assetId || '').trim();
      if (!assetId) errors.assetId = 'Asset ID is required';
    }

    const comment = (values.comment || '').trim();
    if (!comment) errors.comment = 'Comments are required';
    else if (comment.length > 500) errors.comment = 'Character limit exceeded';

    return errors;
  };

  const buildPayload = (values: any) => {
    const payload: any = {};
    if (values.officialEmail !== undefined)
      payload.officialEmail = values.officialEmail;
    if (values.assetId !== undefined) payload.assetId = values.assetId;
    if (values.comment !== undefined) payload.comment = values.comment;
    return payload;
  };

  const handleSave = async (taskId: number, subTaskId: number = 0) => {
    const task = subTaskId
      ? orderedTaskList
          .find((t) => t.id === taskId)
          ?.subTaskDetails?.find((s) => s.id === subTaskId)
      : orderedTaskList.find((t) => t.id === taskId);

    if (!task) return handleError('Task not found.');

    const targetId = subTaskId || taskId;
    const values = taskValues[targetId] || {};
    const errors = validateTask(task, values);

    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, [targetId]: errors }));
      return;
    }

    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[targetId];
      return copy;
    });

    const payload = buildPayload(values);
    setSavingTaskId(targetId);

    try {
      dispatch(AppActions.showLoader());
      const type = subTaskId ? 'S' : 'T';

      await updateOnboardingTask(type, targetId, payload);

      // Update completion state
      setTaskCompletion((prev) => {
        const updated = { ...prev, [targetId]: true };

        if (subTaskId) {
          const parent = orderedTaskList.find((t) => t.id === taskId);
          if (parent?.subTaskDetails?.every((s) => updated[s.id])) {
            updated[`task-${taskId}`] = true;
          }
        }

        return updated;
      });

      // Trigger status change flag to refresh data from backend
      setStatusChangedFlag((prev) => !prev);

      handleSuccess('Task updated successfully');
    } catch (e) {
      handleError('Failed to update the task.');
    } finally {
      dispatch(AppActions.hideLoader());
      setSavingTaskId(null);
    }
  };

  const handleClear = (taskId: number) => {
    const task = orderedTaskList.find((t) => t.id === taskId);
    setTaskValues((prev) => ({
      ...prev,
      [taskId]:
        task?.name === 'Create an Email Account'
          ? { officialEmail: OFFICIAL_EMAIL_DOMAIN }
          : {}
    }));
  };

  const handleStatusChange = async (taskId: number, newLabel: string) => {
    const newStatus = labelToApi(newLabel);
    const task = orderedTaskList.find((t) => t.id === taskId);

    if (!task) {
      handleError('Task not found.');
      return;
    }

    // If setting to ON_HOLD
    if (newStatus === 'ON_HOLD') {
      // Capture the EXACT current timer value before changing status
      const currentTimerValue = timeLeftMap[taskId];
      console.log('Freezing timer at:', currentTimerValue);

      // First, update the UI immediately with the frozen value
      setTimeLeftMap((prev) => ({
        ...prev,
        [taskId]: currentTimerValue
      }));
      if (currentTimerValue) {
        setFrozenTimerValue(taskId, currentTimerValue);
      }

      try {
        dispatch(AppActions.showLoader());

        // Calculate SLA value from current timer
        let slaToStore: string | null = null;
        if (currentTimerValue && currentTimerValue !== '') {
          // Parse the current timer value to minutes (1 working day = 9 hours).
          if (currentTimerValue.includes('d')) {
            // Format: "21d 08:27:53"
            const parts = currentTimerValue.split('d ');
            const days = parseInt(parts[0]) || 0;
            const timeParts = parts[1]?.split(':') || ['0', '0', '0'];
            const hours = parseInt(timeParts[0]) || 0;
            const minutes = parseInt(timeParts[1]) || 0;
            const seconds = parseInt(timeParts[2]) || 0;

            const totalMinutes =
              days * 9 * 60 +
              hours * 60 +
              minutes +
              (seconds > 0 ? 1 : 0);

            slaToStore = String(totalMinutes);
          } else {
            // Format: "08:27:53"
            const timeParts = currentTimerValue.split(':');
            const hours = parseInt(timeParts[0]) || 0;
            const minutes = parseInt(timeParts[1]) || 0;
            const seconds = parseInt(timeParts[2]) || 0;

            const totalMinutes =
              hours * 60 + minutes + (seconds > 0 ? 1 : 0);

            slaToStore = String(totalMinutes);
          }
        }

        // Update task status
        await updateTaskStatus('T', taskId, newStatus);

        // Update local state with frozen SLA
        setOrderedTaskList((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  status: newStatus,
                  sla: slaToStore
                }
              : t
          )
        );

        setStatusChangedFlag((prev) => !prev);
        handleSuccess(
          `Status updated to ${newLabel}. Timer frozen at ${
            currentTimerValue || 'current value'
          }.`
        );
      } catch (e) {
        console.error('Error updating status:', e);
        handleError('Failed to update status.');
      } finally {
        dispatch(AppActions.hideLoader());
      }
      return;
    }

    // For all other status changes
    try {
      dispatch(AppActions.showLoader());
      await updateTaskStatus('T', taskId, newStatus);

      setOrderedTaskList((prev) =>
        prev.map((t) =>
          t.id === taskId ? { ...t, status: newStatus, sla: null } : t
        )
      );
      removeFrozenTimerValue(taskId);
      setStatusChangedFlag((prev) => !prev);
      handleSuccess(`Status updated to ${newLabel}`);
    } catch (e) {
      handleError('Failed to update status.');
    } finally {
      dispatch(AppActions.hideLoader());
    }
  };

  const overallStatus = (tasks: any[]) => {
    if (tasks.some((task) => task.status === 'IN_PROGRESS')) {
      return 'In progress';
    } else if (tasks.every((task) => task.status === 'COMPLETED')) {
      return 'Completed';
    } else {
      return 'Pending';
    }
  };

  const SectionHeader = ({
    title,
    completedTasksCount,
    OnboardingTasks,
    taskCount,
    expanded,
    onToggle,
    statusLabelWidth
  }: any) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bgcolor="#d6d4ff"
      borderRadius={1}
      px={2}
      py={1.5}
      mb={1}
      sx={{ cursor: 'pointer' }}
      onClick={onToggle}
    >
      <Typography fontWeight={600}>{title}</Typography>
      <Box
        display="flex"
        alignItems="center"
        ml="auto"
        mr={2}
        gap={1}
        sx={{ minWidth: statusLabelWidth }}
      >
        <Typography fontWeight={600}>
          ({completedTasksCount}/{taskCount})
        </Typography>
        <Box
          px={1.25}
          py={0.25}
          borderRadius="10px"
          bgcolor="#f0eeff"
          color="#5f5dc4"
          fontWeight={600}
          fontSize={12}
          lineHeight={1.6}
          textTransform="capitalize"
          whiteSpace="nowrap"
          component="span"
          sx={{
            width: statusLabelWidth,
            textAlign: 'center'
          }}
        >
          {getOverallStatusLabel(OnboardingTasks)}
        </Box>
      </Box>
      {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </Box>
  );

  const getFieldsForTask = (taskName: string) => {
    const fields: {
      key: string;
      label: string;
      required?: boolean;
      type?: string;
    }[] = [];

    if (taskName === 'Create an Email Account') {
      fields.push({
        key: 'officialEmail',
        label: 'Official Email',
        required: true
      });
    }
    if (taskName === 'Assign a Laptop') {
      fields.push({ key: 'assetId', label: 'Asset ID', required: true });
    }
    // Always include comment
    fields.push({
      key: 'comment',
      label: 'Comment',
      required: true,
      type: 'textarea'
    });

    return fields;
  };

  const calculateOnboardingProgress = () => {
    const totalTaskCount = orderedTaskList.length;
    if (totalTaskCount === 0) return 0;

    const completedTaskCount =
      preOnboardingCompletedTasksCount + postOnboardingCompletedTasksCount;

    return Math.round((completedTaskCount / totalTaskCount) * 100);
  };

  const checkDependency = (_task: TaskDetail) => {
    return false;
  };

  const renderTask = (task: TaskDetail) => {
    const isExpanded = expandedTaskId === task.id;

    const currentStatus = task.status || 'TO_DO';
    const isBlockedByDependency = checkDependency(task);
    const hasPermission = permissions.some(
      (perm) => perm.name === task.name && perm.value === true
    );
    const isReadOnly = hasPermission
      ? currentStatus === 'COMPLETED' ||
        currentStatus === 'ON_HOLD' ||
        !hasOnboardingStarted ||
        isBlockedByDependency
      : true;

    const values = taskValues[task.id] || {};

    const emailErr = fieldErrors[task.id]?.officialEmail || '';
    const commentErr = fieldErrors[task.id]?.comment || '';
    const assetErr = fieldErrors[task.id]?.assetId || '';

    // ...existing code...

    const dueOrCompletedText =
      normalizeTaskStatus(currentStatus) === 'COMPLETED' ? (
        <>
          Completed Date: <strong>{formatMMDDYYYY(task.completedAt)}</strong>
        </>
      ) : (
        <>
          Due Date: <strong>{formatMMDDYYYY(task.expectedCompletionAt)}</strong>
          {/* Show timer if we have a value */}
          {(() => {
            const timerValue = timeLeftMap[task.id];

            if (timerValue && timerValue !== '') {
              return (
                <>
                  {' '}
                  &nbsp;&nbsp;
                  {timerValue}
                  {timerValue !== 'Overdue' ? ' Left' : ''}
                  {normalizeTaskStatus(currentStatus) === 'ON_HOLD'
                    ? ' (SLA timer stopped as the task status has been changed to On Hold.)'
                    : isOutsideHours()
                    ? ' (SLA timer paused – will resume during working hours (9:00 AM – 6:00 PM))'
                    : ''}
                </>
              );
            }
            return null;
          })()}
        </>
      );

    const mainTaskFields = getFieldsForTask(task.name);

    return (
      <Box key={task.id} sx={{ borderRadius: 1, overflow: 'hidden', mb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          bgcolor="#f5f4ff"
          px={2}
          py={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              checked={currentStatus === 'COMPLETED'}
              disabled={isReadOnly}
            />
            <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
              <Typography fontWeight={600}>{task.name}</Typography>
              <Typography
                fontSize="12px"
                color="text.secondary"
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal'
                }}
              >
                {dueOrCompletedText}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ flexShrink: 0 }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Box sx={{ minWidth: 70 }}>
                <CustomToggleSwitch
                  initial={historyVisibility[task.id] || false}
                  onToggle={(val) =>
                    setHistoryVisibility((prev) => ({
                      ...prev,
                      [task.id]: val
                    }))
                  }
                  onText="Show"
                  offText="Hide"
                  disabled={normalizeTaskStatus(currentStatus) === 'TO_DO'}
                />
              </Box>
              <Box sx={{ width: 140, mr: 1 }}>
                <StatusDropdown
                  status={apiToLabel(currentStatus)}
                  isDisabled={
                    timeLeftMap[task.id] === 'Overdue'
                      ? true
                      : normalizeTaskStatus(currentStatus) === 'ON_HOLD'
                      ? !isReadOnly
                      : isReadOnly
                  }
                  onChange={(s) => handleStatusChange(task.id, s)}
                />
              </Box>
              <IconButton size="small" onClick={() => handleExpand(task.id)}>
                {isExpanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Stack>
          </Box>
        </Box>

        <Collapse in={isExpanded}>
          <Box bgcolor="#fff" px={3} py={3}>
            {task.subTaskDetails && task.subTaskDetails.length > 0 ? (
              [...task.subTaskDetails]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((subTask: SubTaskDetail) => {
                  const subTaskId = subTask.id;
                  const taskId = parseInt(subTask.taskId);
                  const subValues = taskValues[subTaskId] || {};
                  const subCommentErr = fieldErrors[subTaskId]?.comment || '';
                  const isSubReadOnly =
                    subTask.status === 'COMPLETED' ||
                    !hasOnboardingStarted ||
                    isBlockedByDependency;
                  const fields = getFieldsForTask(subTask.name);

                  return (
                    <Box
                      key={subTaskId}
                      sx={{ mb: 3, borderBottom: '1px solid #eee', pb: 2 }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Checkbox
                          checked={subTask.status === 'COMPLETED'}
                          disabled={isSubReadOnly}
                        />
                        <Typography fontWeight={500}>{subTask.name}</Typography>
                      </Box>

                      {fields.map((field) => {
                        const val = (subValues as any)[field.key] || '';
                        const isComment = field.key === 'comment';
                        const errorText = (isComment && subCommentErr) || '';
                        return (
                          <Box key={field.key} sx={{ mb: 2 }}>
                            <Typography
                              sx={{
                                pl: 0.5,
                                pb: 0.5,
                                fontSize: '13px',
                                fontWeight: 500
                              }}
                            >
                              {field.label}
                              {field.required && (
                                <span style={{ color: 'red' }}> *</span>
                              )}
                            </Typography>
                            <TextField
                              placeholder={`Enter ${field.label}`}
                              fullWidth
                              required={!!field.required}
                              multiline={field.type === 'textarea'}
                              rows={field.type === 'textarea' ? 3 : 1}
                              value={val}
                              onChange={(e) =>
                                handleInputChange(
                                  subTaskId ?? task.id,
                                  field.key,
                                  e.target.value
                                )
                              }
                              error={!!errorText}
                              disabled={isSubReadOnly}
                              inputProps={{
                                maxLength: isComment ? 500 : undefined
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '10px'
                                }
                              }}
                            />
                            {errorText && (
                              <Typography sx={errorTextStyle}>
                                {errorText}
                              </Typography>
                            )}
                            {isComment && (
                              <Typography
                                sx={{ fontSize: '11px', color: '#888', mt: 0.5 }}
                              >
                                {val.length}/500
                              </Typography>
                            )}
                          </Box>
                        );
                      })}

                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isSubReadOnly}
                          onClick={() => handleClear(subTaskId)}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSave(taskId, subTaskId)}
                          startIcon={savingTaskId === subTaskId}
                          disabled={isSubReadOnly}
                        >
                          {savingTaskId === subTaskId ? 'Saving…' : 'Save'}
                        </Button>
                      </Box>
                    </Box>
                  );
                })
            ) : (
              <>
                {mainTaskFields.map((field) => {
                  const val = (values as any)[field.key] || '';
                  const isEmail = field.key === 'officialEmail';
                  const isComment = field.key === 'comment';
                  const isAsset = field.key === 'assetId';

                  const errorText =
                    (isEmail && emailErr) ||
                    (isComment && commentErr) ||
                    (isAsset && assetErr) ||
                    '';

                  return (
                    <Box key={field.key} sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          pl: 0.5,
                          pb: 0.5,
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                      >
                        {field.label}
                        {field.required && (
                          <span style={{ color: 'red' }}> *</span>
                        )}
                      </Typography>
                      <TextField
                        placeholder={
                          isEmail
                            ? `name${OFFICIAL_EMAIL_DOMAIN}`
                            : `Enter ${field.label}`
                        }
                        fullWidth
                        required={!!field.required}
                        multiline={field.type === 'textarea'}
                        rows={field.type === 'textarea' ? 3 : 1}
                        value={val}
                        onChange={(e) =>
                          handleInputChange(task.id, field.key, e.target.value)
                        }
                        error={!!errorText}
                        disabled={isReadOnly}
                        inputProps={{
                          maxLength: isComment ? 500 : undefined
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '10px' }
                        }}
                      />
                      {errorText && (
                        <Typography sx={errorTextStyle}>{errorText}</Typography>
                      )}
                      {isComment && (
                        <Typography
                          sx={{ fontSize: '11px', color: '#888', mt: 0.5 }}
                        >
                          {val.length}/500
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  mt={2}
                  mb={2}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isReadOnly}
                    onClick={() => handleClear(task.id)}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={isReadOnly}
                    onClick={() => handleSave(task.id)}
                    startIcon={savingTaskId === task.id}
                  >
                    {savingTaskId === task.id ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </>
            )}

            {historyVisibility[task.id] &&
              (() => {
                const currentTaskHistory = taskHistory[task.id] || [];
                const paginatedHistory = currentTaskHistory.slice(
                  page * limit,
                  page * limit + limit
                );
                const hasSubTask = currentTaskHistory?.some(
                  (entry) => entry.subTask != null
                );
                return (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <CustomTableRow>
                          <TableHeaderCell content="Updated By" smallFont />
                          {hasSubTask && (
                            <TableHeaderCell content="Subtask" smallFont />
                          )}
                          <TableHeaderCell content="Date Of Change" smallFont />
                          <TableHeaderCell content="Status" smallFont />
                          <TableHeaderCell content="Comment" smallFont />
                        </CustomTableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedHistory?.map((historyEntry) => (
                          <TableRow key={historyEntry.id}>
                            <TableColumnCell>
                              <Typography
                                paddingBottom="0.25rem"
                                paddingTop="0.5rem"
                                variant="body2"
                                gutterBottom
                                style={{ fontSize: '11px' }}
                                noWrap
                              >
                                {historyEntry.createdBy}
                              </Typography>
                            </TableColumnCell>

                            {hasSubTask && (
                              <TableColumnCell>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  gutterBottom
                                  style={{ fontSize: '11px' }}
                                >
                                  {historyEntry.subTask ?? '—'}
                                </Typography>
                              </TableColumnCell>
                            )}

                            <TableColumnCell>
                              <Typography
                                paddingBottom="0.25rem"
                                paddingTop="0.5rem"
                                variant="body2"
                                gutterBottom
                                style={{ fontSize: '11px' }}
                                noWrap
                              >
                                {historyEntry.activityDate}
                              </Typography>
                            </TableColumnCell>

                            <TableColumnCell>
                              <Typography
                                paddingBottom="0.25rem"
                                paddingTop="0.5rem"
                                variant="body2"
                                gutterBottom
                                style={{ fontSize: '11px' }}
                                noWrap
                              >
                                {apiToLabel(historyEntry.status)}
                              </Typography>
                            </TableColumnCell>

                            <TableColumnCell>
                              <Typography
                                paddingBottom="0.25rem"
                                paddingTop="0.5rem"
                                variant="body2"
                                gutterBottom
                                style={{ fontSize: '11px' }}
                              >
                                {historyEntry.data?.comment ?? '—'}
                              </Typography>
                            </TableColumnCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}

            {taskHistory[task.id]?.length === 0 && (
              <Typography
                style={{
                  fontSize: '15px',
                  fontWeight: 'bold',
                  margin: '1rem',
                  textAlign: 'center'
                }}
                variant="body2"
              >
                No records found
              </Typography>
            )}
            {taskHistory[task.id]?.length > 10 && (
              <Box p={2}>
                <TablePagination
                  sx={{
                    fontSize: '12px',
                    '.MuiTablePagination-displayedRows': {
                      fontSize: '12px'
                    },
                    '.MuiTablePagination-selectLabel': {
                      fontSize: '12px'
                    }
                  }}
                  component="div"
                  count={taskHistory[task.id]?.length}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                  page={page}
                  rowsPerPage={limit}
                  rowsPerPageOptions={[10, 20, 30, 40, 50]}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Grid
      container
      spacing={0}
      sx={{ width: '100%', margin: 0, flexWrap: 'nowrap' }}
    >
      <Grid item xs={12} md={12}>
        <Box
          width="100%"
          borderRadius={2}
          boxShadow={2}
          p={3}
          bgcolor="#fff"
          sx={{ ml: { xs: 0, md: 0 }, px: { xs: 1, md: 3 } }}
        >
          {/* Header summary */}
          <Box mb={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={0.75}
            >
              <Typography fontSize={14}>
                {calculateOnboardingProgress()}% Completed
              </Typography>
              <Box
                sx={{
                  px: 1.25,
                  py: 0.25,
                  borderRadius: '10px',
                  bgcolor: '#f0eeff',
                  color: '#5f5dc4',
                  fontWeight: 600,
                  fontSize: 12,
                  lineHeight: 1.6,
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap'
                }}
              >
                {onboardingDetails?.status
                  ? apiToLabel(onboardingDetails?.status)
                  : 'Pending'}
              </Box>
            </Box>

            {/* Pass the calculated progress to LinearProgress */}
            <LinearProgress
              variant="determinate"
              value={calculateOnboardingProgress()}
              sx={{
                height: 10,
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  transition: 'all 0.3s ease-in-out'
                },
                '&:hover .MuiLinearProgress-bar': {
                  transform: 'scaleY(1.2)',
                  boxShadow: '0 0 8px rgba(95, 93, 196, 0.6)',
                  borderRadius: 5
                }
              }}
            />

            <Box mt={0.85} display="flex" justifyContent="flex-end">
              <Typography fontSize={13} color="black">
                Expected Joining Date:&nbsp;
                <strong>
                  {expectedJoinDate &&
                    format(new Date(expectedJoinDate), 'MMMM d, yyyy')}
                </strong>
              </Typography>
            </Box>
          </Box>

          <SectionHeader
            title="Pre Onboarding List"
            completedTasksCount={preOnboardingCompletedTasksCount}
            OnboardingTasks={preOnboardingTasks}
            taskCount={preOnboardingTasks.length}
            expanded={preExpanded}
            onToggle={() => setPreExpanded((prev) => !prev)}
            statusLabelWidth={statusLabelWidth}
          />

          <Collapse in={preExpanded}>
            {preOnboardingTasks.length === 0 ? (
              <Typography sx={{ p: 2, color: 'text.secondary' }}>
                No tasks to show.
              </Typography>
            ) : (
              preOnboardingTasks.map((task) => renderTask(task))
            )}
          </Collapse>

          {/* Post-Onboarding Section */}
          <SectionHeader
            title="Post Onboarding List"
            completedTasksCount={postOnboardingCompletedTasksCount}
            OnboardingTasks={postOnboardingTasks}
            taskCount={postOnboardingTasks.length}
            expanded={postExpanded}
            onToggle={() => setPostExpanded((prev) => !prev)}
            statusLabelWidth={statusLabelWidth}
          />

          <Collapse in={postExpanded}>
            {postOnboardingTasks.length === 0 ? (
              <Typography sx={{ p: 2, color: 'text.secondary' }}>
                No tasks to show.
              </Typography>
            ) : (
              postOnboardingTasks.map((task) => renderTask(task))
            )}
          </Collapse>
        </Box>
      </Grid>

      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackbarSeverity={snackbarSeverity}
        snackBarMessage={snackBarMessage}
      />
    </Grid>
  );
};

export default OnboardingTaskList;
